// A `function` step whose long job is watched from the BROWSER
// (docs/plans/wizard-external-website-step.md §5.1; engine wizard/poll.py).
//
// The server started the job and handed back the provider's status address
// (never a credential). This screen polls THAT address directly — Twasta's
// server hears nothing while the job runs — until the model's `done` / `fail`
// condition holds, then asks the server once to fetch the real result. What
// the browser read is only a signal, never the data.
//
// A provider that does not answer browsers (no CORS headers) makes the read
// throw; the screen then asks the server to look instead (`wizard_poll_check`),
// which the server allows at most once per interval.
//
// No poll on this step (the function failed and stopped here): the usual
// stopped-step screen with Try again.

import { useEffect, useRef, useState } from 'react'
import { Loader2, RotateCcw } from 'lucide-react'
import type { WizardPollTicket } from '../contract'
import { text } from '../model'
import { pollMatches } from '../ops'
import { INFO, banner, button, muted } from '../styles'
import AutoStep from './AutoStep'
import type { StepProps } from './common'

const SLOW_AFTER_MS = 5 * 60_000
const MAX_INTERVAL_MS = 60_000

type Mode = 'browser' | 'server'

export default function PollStep(props: StepProps) {
  const { ctl, handheld } = props
  const { step, frame, scope } = ctl
  const ticket = (step && (frame?.steps?.[step.id] as { poll?: WizardPollTicket } | undefined)?.poll) || null
  const [mode, setMode] = useState<Mode>('browser')
  const [note, setNote] = useState('')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const alive = useRef(true)
  const startedAt = useRef(Date.now())
  const modeRef = useRef<Mode>('browser')

  useEffect(() => {
    if (!ticket || !step) return undefined
    alive.current = true
    startedAt.current = Date.now()
    const base = Math.max(2, Number(ticket.interval_s) || 5) * 1000
    const expires = ticket.expires_at ? Date.parse(ticket.expires_at) : NaN

    const wait = () => {
      const late = Date.now() - startedAt.current > SLOW_AFTER_MS
      return late ? Math.min(base * 3, MAX_INTERVAL_MS) : base
    }
    const schedule = () => {
      if (!alive.current) return
      timer.current = setTimeout(() => { void tick() }, wait())
    }
    const finish = async (server: boolean) => {
      const res = await ctl.pollJob(server)
      if (!alive.current) return
      if (res === 'waiting') schedule()
    }
    const tick = async () => {
      if (!alive.current) return
      if (!Number.isNaN(expires) && Date.now() > expires) {
        // The server says so in its own words (and drops the job's record).
        await finish(true)
        return
      }
      if (modeRef.current === 'server') { await finish(true); return }
      let data: unknown
      try {
        const res = await fetch(ticket.url, {
          method: 'GET', mode: 'cors', credentials: 'omit', cache: 'no-store',
          headers: { Accept: 'application/json', ...(ticket.headers || {}) },
        })
        data = await res.json().catch(() => null)
      } catch (err) {
        // Blocked (CORS) or unreachable from here: the server watches instead.
        if (typeof console !== 'undefined') console.info('[wizard] polling through the server:', err)
        modeRef.current = 'server'
        setMode('server')
        await finish(true)
        return
      }
      if (!alive.current) return
      if (pollMatches(data, ticket.done) || pollMatches(data, ticket.fail)) {
        // Done (or failed) as far as the browser can tell — the server fetches
        // the real result, or reports the failure, once.
        await finish(pollMatches(data, ticket.fail))
        return
      }
      setNote(typeof data === 'object' && data ? summary(data) : '')
      schedule()
    }
    void tick()
    return () => {
      alive.current = false
      if (timer.current) clearTimeout(timer.current)
    }
    // Re-armed per job only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.url, step?.id])

  if (!ticket || !step || !scope) return <AutoStep {...props} />
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={banner(INFO)}>
        <Loader2 size={16} className="spin" />
        <span>{step.title ? text(step.title, scope) : 'Working…'} — this continues by itself when it is done.</span>
      </div>
      {note && <div style={muted}>{note}</div>}
      {mode === 'server' && <div style={muted}>Checking through the server.</div>}
      <div>
        <button type="button" style={button(handheld)} onClick={() => void ctl.pollJob(true)}
          disabled={!!ctl.busy} data-tw-action="poll-check">
          <RotateCcw size={16} /> Check now
        </button>
      </div>
    </div>
  )
}

/** A short "status: RUNNING (40%)"-style line from a status answer. */
function summary(data: object): string {
  const d = data as Record<string, unknown>
  const status = d.status ?? d.state ?? d.Status
  const pct = d.progress ?? d.percent
  if (status === undefined) return ''
  return `Status: ${String(status)}${pct !== undefined ? ` (${String(pct)}%)` : ''}`
}
